import type { Domain, UnitType, AuraEffect, OfficerAbility } from './types';

/** Resolve a unit type to its base type (if faction-unique) or itself. */
export function resolveBaseType(unitType: UnitType): UnitType {
  return UNIT_DEFS[unitType]?.baseType ?? unitType;
}

export interface UnitDef {
  type: UnitType;
  label: string;
  movementRange: number;
  attackRange: number;
  minAttackRange?: number;
  sightRange: number;
  attack: number;
  defense: number;
  maxHp: number;
  cost: number;
  trainingTime: number; // turns
  domain: Domain;
  cargoCapacity?: number;
  cargoAccepts?: UnitType[];
  /** Maximum fuel capacity. 0 = unit doesn't burn fuel (foot units). */
  maxFuel: number;
  /** Maximum ammo capacity. 0 = unit can't attack (cargo carriers). */
  maxAmmo: number;
  /** Fuel consumed per turn just for existing — only air/water, land = 0. */
  fuelCostIdle: number;
  /** Fuel consumed per tile of movement (0 = non-motorized foot). */
  fuelCostMove: number;
  /** Ammo consumed per attack action. Counter-attacks are free. */
  ammoCostAttack: number;
  /** Mobile-resupplier capability: the movement domain whose friendly units this
   *  unit can actively top up (fuel + ammo) via the "Zaopatrz" order. Undefined =
   *  not a resupplier. supply-truck→land, carrier/plane-transport→air,
   *  ship-transport→water. Resolved through `resolveBaseType` for faction units. */
  resuppliesDomain?: Domain;
  /** Passive area bonuses projected by this unit (commanders). */
  auras?: AuraEffect[];
  /** Active abilities (officers — e.g. air-strike). */
  abilities?: OfficerAbility[];
  /** Whether the unit can be trained at a building. Default true. Heroes/officers
   *  use it together with roster rules (per-player hero limit). */
  recruitable?: boolean;
  /** For faction-unique units: the base generic type this unit is derived from. */
  baseType?: UnitType;
}

/** Build a hero unit def — modest combat stats, recruitable at HQ, with auras. */
function heroDef(type: UnitType, auras: AuraEffect[]): UnitDef {
  return {
    type, label: type,
    movementRange: 3, attackRange: 1, sightRange: 2,
    attack: 5, defense: 4, maxHp: 12, cost: 1500, trainingTime: 3, domain: 'land',
    maxFuel: 0, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
    recruitable: true, auras,
  };
}

export const UNIT_DEFS: Record<UnitType, UnitDef> = {
  // ---- LAND: light / infantry ----
  infantry: {
    type: 'infantry',
    label: 'infantry',
    movementRange: 3,
    attackRange: 1,
    sightRange: 2,
    attack: 10,
    defense: 3,
    maxHp: 10,
    cost: 100,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 8, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  'infantry-bazooka': {
    type: 'infantry-bazooka',
    label: 'infantry-bazooka',
    movementRange: 3,
    attackRange: 1,
    sightRange: 2,
    attack: 15,
    defense: 3,
    maxHp: 10,
    cost: 180,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 8, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  recon: {
    type: 'recon',
    label: 'recon',
    movementRange: 5,
    attackRange: 1,
    sightRange: 5,
    attack: 6,
    defense: 2,
    maxHp: 8,
    cost: 80,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  cavalry: {
    type: 'cavalry',
    label: 'cavalry',
    movementRange: 8,
    attackRange: 1,
    sightRange: 3,
    attack: 7,
    defense: 3,
    maxHp: 10,
    cost: 120,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 10, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  jeep: {
    type: 'jeep',
    label: 'jeep',
    movementRange: 5,
    attackRange: 1,
    sightRange: 2,
    attack: 8,
    defense: 4,
    maxHp: 12,
    cost: 150,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 12, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- LAND: armour ----
  'light-tank': {
    type: 'light-tank',
    label: 'light-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 10,
    defense: 4,
    maxHp: 12,
    cost: 200,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'medium-tank': {
    type: 'medium-tank',
    label: 'medium-tank',
    movementRange: 6,
    attackRange: 1,
    sightRange: 2,
    attack: 12,
    defense: 6,
    maxHp: 20,
    cost: 300,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 15, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'heavy-tank': {
    type: 'heavy-tank',
    label: 'heavy-tank',
    movementRange: 4,
    attackRange: 1,
    sightRange: 2,
    attack: 18,
    defense: 8,
    maxHp: 25,
    cost: 500,
    trainingTime: 3,
    domain: 'land',
    maxFuel: 18, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 2, ammoCostAttack: 1,
  },
  apc: {
    type: 'apc',
    label: 'apc',
    movementRange: 5,
    attackRange: 0,
    sightRange: 2,
    attack: 0,
    defense: 8,
    maxHp: 15,
    cost: 200,
    trainingTime: 2,
    domain: 'land',
    cargoCapacity: 1,
    cargoAccepts: ['infantry', 'infantry-bazooka'],
    maxFuel: 12, maxAmmo: 0, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 0,
  },
  'aa-flakpanzer': {
    type: 'aa-flakpanzer',
    label: 'aa-flakpanzer',
    movementRange: 5,
    attackRange: 1,
    sightRange: 3,
    attack: 8,
    defense: 4,
    maxHp: 12,
    cost: 250,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  'tank-destroyer': {
    type: 'tank-destroyer',
    label: 'tank-destroyer',
    movementRange: 6,
    attackRange: 1,
    sightRange: 3,
    attack: 16,
    defense: 3,
    maxHp: 12,
    cost: 280,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- LAND: artillery ----
  'long-range-artillery': {
    type: 'long-range-artillery',
    label: 'long-range-artillery',
    movementRange: 2,
    attackRange: 5,
    minAttackRange: 3,
    sightRange: 3,
    attack: 15,
    defense: 1,
    maxHp: 8,
    cost: 350,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 8, maxAmmo: 4, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'anti-tank-artillery': {
    type: 'anti-tank-artillery',
    label: 'anti-tank-artillery',
    movementRange: 2,
    attackRange: 2,
    minAttackRange: 1,
    sightRange: 2,
    attack: 20,
    defense: 2,
    maxHp: 10,
    cost: 300,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 8, maxAmmo: 4, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- LAND: support ----
  'supply-truck': {
    type: 'supply-truck',
    label: 'supply-truck',
    movementRange: 4,
    attackRange: 0,
    sightRange: 2,
    attack: 0,
    defense: 2,
    maxHp: 10,
    cost: 150,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 12, maxAmmo: 0, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 0,
    resuppliesDomain: 'land',
  },
  medic: {
    type: 'medic',
    label: 'medic',
    movementRange: 4,
    attackRange: 0,
    sightRange: 2,
    attack: 0,
    defense: 2,
    maxHp: 10,
    cost: 200,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 0, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 0,
  },

  // ---- AIR ----
  'plane-fighter': {
    type: 'plane-fighter',
    label: 'plane-fighter',
    movementRange: 9,
    attackRange: 1,
    sightRange: 4,
    attack: 16,
    defense: 2,
    maxHp: 10,
    cost: 400,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 8, maxAmmo: 5, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'plane-bomber': {
    type: 'plane-bomber',
    label: 'plane-bomber',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 20,
    defense: 1,
    maxHp: 12,
    cost: 500,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 10, maxAmmo: 4, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'plane-transport': {
    type: 'plane-transport',
    label: 'plane-transport',
    movementRange: 8,
    attackRange: 0,
    sightRange: 3,
    attack: 0,
    defense: 0,
    maxHp: 10,
    cost: 250,
    trainingTime: 2,
    domain: 'air',
    cargoCapacity: 1,
    cargoAccepts: ['infantry', 'infantry-bazooka'],
    maxFuel: 10, maxAmmo: 0, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 0,
    resuppliesDomain: 'air',
  },

  // ---- WATER ----
  'ship-battleship': {
    type: 'ship-battleship',
    label: 'ship-battleship',
    movementRange: 5,
    attackRange: 3,
    sightRange: 3,
    attack: 14,
    defense: 6,
    maxHp: 15,
    cost: 500,
    trainingTime: 3,
    domain: 'water',
    maxFuel: 15, maxAmmo: 8, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'ship-submarine': {
    type: 'ship-submarine',
    label: 'ship-submarine',
    movementRange: 6,
    attackRange: 2,
    sightRange: 2,
    attack: 12,
    defense: 3,
    maxHp: 12,
    cost: 300,
    trainingTime: 2,
    domain: 'water',
    maxFuel: 12, maxAmmo: 5, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'ship-transport': {
    type: 'ship-transport',
    label: 'ship-transport',
    movementRange: 4,
    attackRange: 0,
    sightRange: 2,
    attack: 0,
    defense: 4,
    maxHp: 12,
    cost: 200,
    trainingTime: 2,
    domain: 'water',
    cargoCapacity: 2,
    cargoAccepts: ['infantry', 'infantry-bazooka', 'recon', 'cavalry', 'jeep', 'anti-tank-artillery',
      'light-tank', 'medium-tank', 'heavy-tank', 'apc', 'aa-flakpanzer', 'tank-destroyer', 'supply-truck', 'medic'],
    maxFuel: 12, maxAmmo: 0, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 0,
    resuppliesDomain: 'water',
  },
  'ship-aircraft-carrier': {
    type: 'ship-aircraft-carrier',
    label: 'ship-aircraft-carrier',
    movementRange: 4,
    attackRange: 0,
    sightRange: 4,
    attack: 0,
    defense: 6,
    maxHp: 20,
    cost: 450,
    trainingTime: 3,
    domain: 'water',
    cargoCapacity: 2,
    cargoAccepts: ['plane-fighter', 'plane-bomber', 'plane-transport'],
    // Unarmed transport-and-resupply vessel (like the APC): attack/range already 0,
    // ammo zeroed so it neither attacks nor hoards unusable rounds. Resupplies the
    // planes it ferries — and adjacent deployed planes — via the active order.
    maxFuel: 15, maxAmmo: 0, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 0,
    resuppliesDomain: 'air',
  },

  // ======== FACTION-UNIQUE UNITS ========

  // ---- Polska ----
  'polska-ulani': {
    type: 'polska-ulani',
    label: 'polska-ulani',
    baseType: 'cavalry',
    movementRange: 9,
    attackRange: 1,
    sightRange: 4,
    attack: 9,
    defense: 4,
    maxHp: 12,
    cost: 160,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 10, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  'polska-7tp': {
    type: 'polska-7tp',
    label: 'polska-7tp',
    baseType: 'light-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 12,
    defense: 5,
    maxHp: 14,
    cost: 240,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- ZSRR ----
  'zsrr-t34': {
    type: 'zsrr-t34',
    label: 'zsrr-t34',
    baseType: 'medium-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 14,
    defense: 7,
    maxHp: 22,
    cost: 350,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 15, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'zsrr-katiusza': {
    type: 'zsrr-katiusza',
    label: 'zsrr-katiusza',
    baseType: 'long-range-artillery',
    movementRange: 2,
    attackRange: 6,
    minAttackRange: 3,
    sightRange: 3,
    attack: 18,
    defense: 1,
    maxHp: 10,
    cost: 400,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 10, maxAmmo: 4, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- III Rzesza ----
  'iii-rzesza-tiger': {
    type: 'iii-rzesza-tiger',
    label: 'iii-rzesza-tiger',
    baseType: 'heavy-tank',
    movementRange: 4,
    attackRange: 1,
    sightRange: 2,
    attack: 22,
    defense: 10,
    maxHp: 28,
    cost: 600,
    trainingTime: 3,
    domain: 'land',
    maxFuel: 18, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 2, ammoCostAttack: 1,
  },
  'iii-rzesza-stuka': {
    type: 'iii-rzesza-stuka',
    label: 'iii-rzesza-stuka',
    baseType: 'plane-bomber',
    movementRange: 8,
    attackRange: 1,
    sightRange: 4,
    attack: 24,
    defense: 2,
    maxHp: 14,
    cost: 550,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 9, maxAmmo: 4, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- USA ----
  'usa-sherman': {
    type: 'usa-sherman',
    label: 'usa-sherman',
    baseType: 'medium-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 13,
    defense: 7,
    maxHp: 22,
    cost: 320,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 15, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'usa-lcvp-higgins': {
    type: 'usa-lcvp-higgins',
    label: 'usa-lcvp-higgins',
    baseType: 'ship-transport',
    movementRange: 5,
    attackRange: 0,
    sightRange: 2,
    attack: 0,
    defense: 5,
    maxHp: 14,
    cost: 220,
    trainingTime: 2,
    domain: 'water',
    cargoCapacity: 3,
    cargoAccepts: ['infantry', 'infantry-bazooka', 'recon', 'cavalry', 'jeep', 'anti-tank-artillery',
      'light-tank', 'medium-tank', 'heavy-tank', 'apc', 'aa-flakpanzer', 'tank-destroyer', 'supply-truck', 'medic'],
    maxFuel: 12, maxAmmo: 0, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 0,
  },

  // ---- Włochy ----
  'wlochy-l3-33': {
    type: 'wlochy-l3-33',
    label: 'wlochy-l3-33',
    baseType: 'light-tank',
    movementRange: 8,
    attackRange: 1,
    sightRange: 3,
    attack: 11,
    defense: 5,
    maxHp: 14,
    cost: 230,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 10, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'wlochy-sm79-sparviero': {
    type: 'wlochy-sm79-sparviero',
    label: 'wlochy-sm79-sparviero',
    baseType: 'plane-fighter',
    movementRange: 10,
    attackRange: 1,
    sightRange: 5,
    attack: 18,
    defense: 3,
    maxHp: 12,
    cost: 450,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 10, maxAmmo: 4, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- Japonia ----
  'japonia-chi-ha': {
    type: 'japonia-chi-ha',
    label: 'japonia-chi-ha',
    baseType: 'medium-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 13,
    defense: 7,
    maxHp: 22,
    cost: 340,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'japonia-a6m-zero': {
    type: 'japonia-a6m-zero',
    label: 'japonia-a6m-zero',
    baseType: 'plane-fighter',
    movementRange: 11,
    attackRange: 1,
    sightRange: 5,
    attack: 18,
    defense: 2,
    maxHp: 10,
    cost: 430,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 8, maxAmmo: 5, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- Francja ----
  'francja-renault-r35': {
    type: 'francja-renault-r35',
    label: 'francja-renault-r35',
    baseType: 'light-tank',
    movementRange: 7,
    attackRange: 1,
    sightRange: 3,
    attack: 12,
    defense: 6,
    maxHp: 14,
    cost: 240,
    trainingTime: 2,
    domain: 'land',
    maxFuel: 12, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },
  'francja-ms406': {
    type: 'francja-ms406',
    label: 'francja-ms406',
    baseType: 'plane-fighter',
    movementRange: 10,
    attackRange: 1,
    sightRange: 5,
    attack: 17,
    defense: 3,
    maxHp: 12,
    cost: 430,
    trainingTime: 2,
    domain: 'air',
    maxFuel: 8, maxAmmo: 5, fuelCostIdle: 1, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- Jugosławia ----
  'jugoslawia-partyzanci': {
    type: 'jugoslawia-partyzanci',
    label: 'jugoslawia-partyzanci',
    baseType: 'infantry',
    movementRange: 4,
    attackRange: 1,
    sightRange: 3,
    attack: 12,
    defense: 4,
    maxHp: 12,
    cost: 130,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 8, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  'jugoslawia-ba64': {
    type: 'jugoslawia-ba64',
    label: 'jugoslawia-ba64',
    baseType: 'recon',
    movementRange: 6,
    attackRange: 1,
    sightRange: 6,
    attack: 8,
    defense: 3,
    maxHp: 10,
    cost: 110,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 12, maxAmmo: 6, fuelCostIdle: 0, fuelCostMove: 1, ammoCostAttack: 1,
  },

  // ---- Wielka Brytania ----
  'wielka-brytania-komandosi': {
    type: 'wielka-brytania-komandosi',
    label: 'wielka-brytania-komandosi',
    baseType: 'infantry',
    movementRange: 4,
    attackRange: 1,
    sightRange: 3,
    attack: 14,
    defense: 5,
    maxHp: 12,
    cost: 150,
    trainingTime: 1,
    domain: 'land',
    maxFuel: 0, maxAmmo: 8, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
  },
  'wielka-brytania-churchill': {
    type: 'wielka-brytania-churchill',
    label: 'wielka-brytania-churchill',
    baseType: 'heavy-tank',
    movementRange: 4,
    attackRange: 1,
    sightRange: 2,
    attack: 20,
    defense: 10,
    maxHp: 30,
    cost: 550,
    trainingTime: 3,
    domain: 'land',
    maxFuel: 18, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 2, ammoCostAttack: 1,
  },

  // ======== COMMANDERS (officer + per-faction heroes) ========
  officer: {
    type: 'officer', label: 'officer',
    movementRange: 3, attackRange: 1, sightRange: 2,
    attack: 4, defense: 3, maxHp: 8, cost: 700, trainingTime: 2, domain: 'land',
    maxFuel: 0, maxAmmo: 5, fuelCostIdle: 0, fuelCostMove: 0, ammoCostAttack: 1,
    recruitable: true,
    auras: [{ range: 1, target: 'allies', effect: { defenseMultiplier: 1.10 } }],
    abilities: [
      { id: 'air-strike', cooldown: 5, cost: 100, effect: 'air-strike' },
      { id: 'recon', cooldown: 4, cost: 50, effect: 'recon' },
    ],
  },
  // Each hero carries a distinct aura tied to their historical character.
  'hero-polska-a': heroDef('hero-polska-a', [{ range: 3, target: 'allies', effect: { healPerTurn: 1 } }]),       // Curie — leczenie
  'hero-polska-b': heroDef('hero-polska-b', [{ range: 2, target: 'allies', effect: { defenseMultiplier: 1.15 } }]), // Czochralski — pancerz
  'hero-zsrr-a': heroDef('hero-zsrr-a', [{ range: 2, target: 'allies', effect: { movementBonus: 1 } }]),          // Ciołkowski — mobilność
  'hero-zsrr-b': heroDef('hero-zsrr-b', [{ range: 2, target: 'allies', effect: { attackMultiplier: 1.10 } }]),    // Pawłow — agresja
  'hero-iii-rzesza-a': heroDef('hero-iii-rzesza-a', [{ range: 2, target: 'allies', effect: { attackMultiplier: 1.15 } }]), // Haber — siła ognia
  'hero-iii-rzesza-b': heroDef('hero-iii-rzesza-b', [{ range: 2, target: 'allies', effect: { healPerTurn: 1 } }]),         // Landsteiner — leczenie
  'hero-usa-a': heroDef('hero-usa-a', [{ range: 2, target: 'allies', effect: { sightBonus: 1 } }]),               // Edison — wzrok
  'hero-usa-b': heroDef('hero-usa-b', [{ range: 2, target: 'allies', effect: { movementBonus: 1 } }]),            // Goddard — mobilność
  'hero-wlochy-a': heroDef('hero-wlochy-a', [{ range: 3, target: 'allies', effect: { sightBonus: 1 } }]),         // Marconi — łączność/wzrok
  'hero-wlochy-b': heroDef('hero-wlochy-b', [{ range: 2, target: 'allies', effect: { defenseMultiplier: 1.10 } }]), // Volterra — obrona
  'hero-japonia-a': heroDef('hero-japonia-a', [{ range: 2, target: 'allies', effect: { healPerTurn: 1 } }]),      // Noguchi — leczenie
  'hero-japonia-b': heroDef('hero-japonia-b', [{ range: 2, target: 'allies', effect: { defenseMultiplier: 1.10 } }]), // Kitasato — obrona
  'hero-francja-a': heroDef('hero-francja-a', [{ range: 3, target: 'allies', effect: { sightBonus: 1 } }]),       // Perrin — wzrok
  'hero-francja-b': heroDef('hero-francja-b', [{ range: 2, target: 'allies', effect: { healPerTurn: 1 } }]),      // Carrel — leczenie
  'hero-jugoslawia-a': heroDef('hero-jugoslawia-a', [{ range: 3, target: 'allies', effect: { sightBonus: 1 } }]), // Tesla — wzrok
  'hero-jugoslawia-b': heroDef('hero-jugoslawia-b', [{ range: 2, target: 'allies', effect: { attackMultiplier: 1.10 } }]), // Pupin — atak
  'hero-wielka-brytania-a': heroDef('hero-wielka-brytania-a', [{ range: 2, target: 'allies', effect: { attackMultiplier: 1.15 } }]), // Rutherford — siła ognia
  'hero-wielka-brytania-b': heroDef('hero-wielka-brytania-b', [{ range: 2, target: 'allies', effect: { sightBonus: 1 } }]), // Thomson — wzrok
};
